import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { dashboardAPI, workHoursAPI, bookingsAPI } from '../services/api';
import api from '../services/api';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import { 
  CalendarDays, Users, TrendingUp, CreditCard, Play, Square, ArrowRight, Cake, 
  Plane, FileCheck, MessageSquare, UserCheck, Phone, Bell, AlertTriangle, 
  ShieldAlert, Calendar, ChevronRight, Loader2, RefreshCw, Wallet, PiggyBank
} from 'lucide-react';
import TasksPanel from '../components/TasksPanel';
import DepositTodayButton from '../components/DepositTodayButton';
import TrainingWidget from '../components/TrainingWidget';
import CashFlowWidgets from '../components/CashFlowWidgets';
import { useAutoRefresh } from '../context/RefreshContext';

const DashboardPage = () => {
  const { user, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [shift, setShift] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upcomingDepartures, setUpcomingDepartures] = useState([]);
  const [loadingDepartures, setLoadingDepartures] = useState(true);
  const [finance, setFinance] = useState(null);
  
  // Notifications state
  const [notifications, setNotifications] = useState([]);
  const [notifSummary, setNotifSummary] = useState(null);
  const [loadingNotifs, setLoadingNotifs] = useState(true);

  const fetchData = async () => {
    try {
      const [statsRes, shiftRes, departuresRes, birthdaysRes] = await Promise.all([
        dashboardAPI.getStats(), 
        workHoursAPI.getCurrent(),
        dashboardAPI.getUpcomingDepartures(),
        dashboardAPI.getBirthdays(7)
      ]);
      
      // Combine stats with birthdays data
      const statsData = statsRes.data || {};
      const birthdays = (birthdaysRes.data || []).map(b => ({
        name: `${b.first_name} ${b.last_name}`,
        birth_date: b.birth_date,
        days_until: b.days_until,
        phone: b.phone || null
      }));
      
      setStats({ ...statsData, birthdays });
      setShift(shiftRes.data?.shift || null);
      setUpcomingDepartures(departuresRes.data || []);
      setLoadingDepartures(false);
      // Finance KPIs (graceful fallback)
      try {
        const fin = await api.get('/finance/dashboard');
        setFinance(fin.data || null);
      } catch (e) {
        setFinance(null);
      }
    } catch (err) {
      console.error('Dashboard fetch error:', err);
      toast.error('Błąd pobierania danych');
    } finally {
      setLoading(false);
    }
  };

  const fetchNotifications = async () => {
    if (!isAdmin && user?.role !== 'kierownik') return;
    setLoadingNotifs(true);
    try {
      const res = await api.get('/admin/notifications-panel');
      setNotifications(res.data.notifications || []);
      setNotifSummary(res.data.summary || null);
    } catch (err) {
      console.error('Notifications fetch error:', err);
    } finally {
      setLoadingNotifs(false);
    }
  };

  useEffect(() => { fetchData(); fetchNotifications(); }, []);

  // Global auto-refresh: every 30s + on cross-page invalidation
  const refreshDashboard = useCallback(async () => {
    await Promise.all([fetchData(), fetchNotifications()]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useAutoRefresh(refreshDashboard, { scope: 'dashboard', interval: 30000, runOnMount: false });

  const handleShift = async (action) => {
    try {
      await workHoursAPI.record({ action });
      toast.success(action === 'start' ? 'Zmiana rozpoczęta' : 'Zmiana zakończona');
      fetchData();
    } catch (err) {
      const d = err.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : 'Błąd zmiany');
    }
  };

  const handleCheckboxChange = async (bookingId, field, currentValue) => {
    try {
      await dashboardAPI.updateBookingFlags(bookingId, { [field]: !currentValue });
      toast.success('Status zaktualizowany');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Błąd aktualizacji');
    }
  };

  const handleCheck = async (bookingId, checkType, checked) => {
    try {
      await bookingsAPI.updateCheck(bookingId, { check_type: checkType, checked });
      toast.success('Status zaktualizowany');
      fetchData();
    } catch { toast.error('Błąd aktualizacji'); }
  };

  const fmtDate = useCallback((d) => { if (!d) return '-'; const p = d.split('-'); return p.length === 3 ? `${p[2]}.${p[1]}.${p[0]}` : d; }, []);
  const fmtPLN = useCallback((v) => new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' }).format(v || 0), []);

  const today = useMemo(() => new Date(), []);
  const dateStr = useMemo(() => {
    const dayNames = ['Niedziela','Poniedziałek','Wtorek','Środa','Czwartek','Piątek','Sobota'];
    return `${dayNames[today.getDay()]}, ${String(today.getDate()).padStart(2,'0')}.${String(today.getMonth()+1).padStart(2,'0')}.${today.getFullYear()}`;
  }, [today]);

  if (loading) return (
    <div className="space-y-6 p-4 lg:p-6" data-testid="dashboard-skeleton">
      <Skeleton className="h-32 w-full rounded-2xl" />
      <Skeleton className="h-16 w-full rounded-xl" />
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {[1,2,3,4].map(i => <Skeleton key={i} className="h-24 rounded-xl" />)}
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Skeleton className="h-64 rounded-xl" />
        <Skeleton className="h-64 rounded-xl" />
      </div>
    </div>
  );

  const shiftActive = shift?.status === 'aktywna';
  const shiftEnded = shift?.status === 'zakończona';

  return (
    <div className="space-y-6 p-4 lg:p-6" data-testid="dashboard-page">
      {/* Hero */}
      <div className="bg-gradient-to-r from-navy-800 to-navy-700 rounded-2xl p-6 text-white">
        <h1 className="text-2xl font-bold font-heading">Witaj, {user?.full_name || 'Użytkownik'}!</h1>
        <p className="text-navy-200 mt-1 text-sm">Przegląd agencji. Zarządzaj rezerwacjami, śledź płatności i monitoruj wyniki zespołu.</p>
        <div className="flex gap-3 mt-4">
          <Button variant="secondary" size="sm" onClick={() => navigate('/bookings')} className="bg-gold-500/20 hover:bg-gold-500/30 text-gold-300 border border-gold-500/30">
            <CalendarDays className="w-4 h-4 mr-2" />Rezerwacje
          </Button>
          <Button variant="secondary" size="sm" onClick={() => navigate('/clients')} className="bg-gold-500/20 hover:bg-gold-500/30 text-gold-300 border border-gold-500/30">
            <Users className="w-4 h-4 mr-2" />Klienci
          </Button>
        </div>
      </div>

      {/* Cash-flow KPIs (gotówka w biurze / wpłaty dziś / do rozliczenia) */}
      <CashFlowWidgets onClickToSettle={() => navigate('/settlements')} />

      {/* Shift tracker */}
      <Card className="border-l-4 border-l-gold-500">
        <CardContent className="py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-full flex items-center justify-center ${shiftActive ? 'bg-emerald-100 text-emerald-600' : 'bg-slate-100 text-slate-400'}`}>
              {shiftActive ? <Play className="w-4 h-4" /> : <Square className="w-4 h-4" />}
            </div>
            <div>
              <p className="font-medium text-navy-800">{shiftActive ? `Zmiana aktywna (od ${shift?.start_time?.slice(0,5)})` : shiftEnded ? `Zmiana zakończona (${shift?.total_hours?.toFixed(1)}h)` : 'Brak aktywnej zmiany'}</p>
              <p className="text-xs text-muted-foreground">{dateStr}</p>
            </div>
          </div>
          {!shiftActive && !shiftEnded && (
            <Button data-testid="start-shift-btn" onClick={() => handleShift('start')} className="bg-emerald-600 hover:bg-emerald-700">
              <Play className="w-4 h-4 mr-2" />Rozpocznij zmianę
            </Button>
          )}
          {shiftActive && (
            <Button data-testid="end-shift-btn" onClick={() => handleShift('end')} variant="destructive">
              <Square className="w-4 h-4 mr-2" />Zakończ zmianę
            </Button>
          )}
        </CardContent>
      </Card>

      {/* Quick action: deposit today's cash */}
      <DepositTodayButton />

      {/* Training continuity widget */}
      <TrainingWidget />

      {/* Admin Notifications Panel */}
      {(isAdmin || user?.role === 'kierownik') && (
        <Card className="border-l-4 border-l-amber-500" data-testid="notifications-panel">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center gap-2 text-navy-800">
                <Bell className="w-5 h-5 text-amber-500" />
                Panel powiadomień
                {notifSummary?.total > 0 && (
                  <Badge className="bg-amber-500 text-white ml-2">{notifSummary.total}</Badge>
                )}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={fetchNotifications} disabled={loadingNotifs}>
                <RefreshCw className={`w-4 h-4 ${loadingNotifs ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            {loadingNotifs ? (
              <div className="flex items-center justify-center py-6">
                <Loader2 className="w-6 h-6 animate-spin text-slate-400" />
              </div>
            ) : notifications.length === 0 ? (
              <div className="text-center py-6 text-slate-500">
                <Bell className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p>Brak nowych powiadomień</p>
              </div>
            ) : (
              <div className="space-y-2">
                {/* Summary badges */}
                {notifSummary && (
                  <div className="flex flex-wrap gap-2 mb-4 pb-3 border-b">
                    {notifSummary.critical > 0 && (
                      <Badge className="bg-red-100 text-red-700">
                        <AlertTriangle className="w-3 h-3 mr-1" />
                        {notifSummary.critical} krytycznych
                      </Badge>
                    )}
                    {notifSummary.overdue_payments > 0 && (
                      <Badge className="bg-orange-100 text-orange-700">
                        <CreditCard className="w-3 h-3 mr-1" />
                        {notifSummary.overdue_payments} zaległych płatności
                      </Badge>
                    )}
                    {notifSummary.new_bookings > 0 && (
                      <Badge className="bg-blue-100 text-blue-700">
                        <Plane className="w-3 h-3 mr-1" />
                        {notifSummary.new_bookings} nowych rezerwacji
                      </Badge>
                    )}
                    {notifSummary.pending_leaves > 0 && (
                      <Badge className="bg-purple-100 text-purple-700">
                        <UserCheck className="w-3 h-3 mr-1" />
                        {notifSummary.pending_leaves} urlopów do akceptacji
                      </Badge>
                    )}
                  </div>
                )}
                
                {/* Notification items */}
                <div className="max-h-80 overflow-y-auto space-y-2">
                  {notifications.slice(0, 10).map((notif) => (
                    <div 
                      key={notif.id} 
                      className={`p-3 rounded-lg border cursor-pointer hover:shadow-sm transition-shadow ${
                        notif.priority === 'critical' ? 'bg-red-50 border-red-200' :
                        notif.priority === 'high' ? 'bg-amber-50 border-amber-200' :
                        notif.priority === 'medium' ? 'bg-blue-50 border-blue-200' :
                        'bg-slate-50 border-slate-200'
                      }`}
                      onClick={() => notif.link && navigate(notif.link)}
                    >
                      <div className="flex items-start gap-3">
                        <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${
                          notif.priority === 'critical' ? 'bg-red-100 text-red-600' :
                          notif.priority === 'high' ? 'bg-amber-100 text-amber-600' :
                          notif.priority === 'medium' ? 'bg-blue-100 text-blue-600' :
                          'bg-slate-100 text-slate-600'
                        }`}>
                          {notif.type === 'new_booking' && <Plane className="w-4 h-4" />}
                          {notif.type === 'overdue_payment' && <AlertTriangle className="w-4 h-4" />}
                          {notif.type === 'upcoming_departure' && <Calendar className="w-4 h-4" />}
                          {notif.type === 'pending_leave' && <UserCheck className="w-4 h-4" />}
                          {notif.type === 'trip_almost_full' && <Users className="w-4 h-4" />}
                          {notif.type === 'security_alert' && <ShieldAlert className="w-4 h-4" />}
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="font-medium text-sm text-slate-800 truncate">{notif.title}</p>
                          <p className="text-xs text-slate-500 truncate">{notif.message}</p>
                        </div>
                        <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                      </div>
                    </div>
                  ))}
                </div>
                
                {notifications.length > 10 && (
                  <p className="text-center text-xs text-slate-500 pt-2">
                    ...i {notifications.length - 10} więcej powiadomień
                  </p>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Stats cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: 'Rezerwacje', value: stats?.total_bookings || 0, icon: CalendarDays, bg: 'bg-navy-100', iconCls: 'text-navy-600' },
          { label: 'Klienci', value: stats?.total_clients || 0, icon: Users, bg: 'bg-blue-100', iconCls: 'text-blue-600' },
          { label: 'Sprzedaż', value: fmtPLN(stats?.total_revenue), icon: TrendingUp, bg: 'bg-emerald-100', iconCls: 'text-emerald-600' },
          { label: 'Do zapłaty', value: fmtPLN(stats?.pending_payments), icon: CreditCard, bg: 'bg-gold-100', iconCls: 'text-gold-700', sub: 'Oczekujące płatności' },
        ].map((s, i) => (
          <Card key={i} className="border-0 shadow-sm" data-testid={`stat-card-${s.label.toLowerCase()}`}>
            <CardContent className="p-5 flex items-center justify-between">
              <div>
                <p className="text-sm text-muted-foreground">{s.label}</p>
                <p className="text-2xl font-bold text-navy-800 mt-1">{s.value}</p>
                {s.sub && <p className="text-xs text-muted-foreground mt-1">{s.sub}</p>}
              </div>
              <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.bg}`}>
                <s.icon className={`w-6 h-6 ${s.iconCls}`} />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* Finance KPI cards (Dochód Netto) */}
      {finance && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-testid="finance-kpi-row">
          {[
            { label: 'Dochód Netto / m-c', value: fmtPLN(finance.monthly?.net_income), icon: PiggyBank, bg: 'bg-emerald-100', iconCls: 'text-emerald-700', sub: finance.monthly?.mom_delta_pct != null ? `${finance.monthly.mom_delta_pct > 0 ? '+' : ''}${finance.monthly.mom_delta_pct}% vs. poprz. mies.` : null, tid: 'kpi-net-month' },
            { label: 'Dochód Netto / rok', value: fmtPLN(finance.yearly?.net_income), icon: TrendingUp, bg: 'bg-blue-100', iconCls: 'text-blue-700', sub: finance.yearly?.avg_margin_pct != null ? `Marża śr. ${finance.yearly.avg_margin_pct}%` : null, tid: 'kpi-net-year' },
            { label: 'Prowizje oczekujące', value: fmtPLN(finance.yearly?.commission_pending), icon: Wallet, bg: 'bg-amber-100', iconCls: 'text-amber-700', sub: `${finance.counts?.bookings_unsettled || 0} nierozliczonych`, tid: 'kpi-comm-pending' },
            { label: 'Prowizje rozliczone', value: fmtPLN(finance.yearly?.commission_settled), icon: FileCheck, bg: 'bg-green-100', iconCls: 'text-green-700', sub: `${finance.counts?.bookings_settled || 0} rozliczonych`, tid: 'kpi-comm-settled' },
          ].map((s, i) => (
            <Card key={i} className="border-0 shadow-sm cursor-pointer hover:shadow-md transition-shadow" data-testid={s.tid} onClick={() => navigate('/finanse')}>
              <CardContent className="p-5 flex items-center justify-between">
                <div>
                  <p className="text-sm text-muted-foreground">{s.label}</p>
                  <p className="text-2xl font-bold text-navy-800 mt-1">{s.value}</p>
                  {s.sub && <p className="text-xs text-muted-foreground mt-1">{s.sub}</p>}
                </div>
                <div className={`w-12 h-12 rounded-xl flex items-center justify-center ${s.bg}`}>
                  <s.icon className={`w-6 h-6 ${s.iconCls}`} />
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
      {isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Birthdays */}
          <Card data-testid="birthdays-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base flex items-center gap-2"><Cake className="w-5 h-5 text-pink-500" />Urodziny klientów (7 dni)</CardTitle>
            </CardHeader>
            <CardContent>
              {!stats?.birthdays?.length ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Brak urodzin w najbliższym tygodniu</p>
              ) : (
                <div className="space-y-3">
                  {stats.birthdays.map((b, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-pink-50 rounded-lg">
                      <div>
                        <p className="font-medium text-navy-800 text-sm">{b.name}</p>
                        <p className="text-xs text-muted-foreground">{fmtDate(b.birth_date)} {b.days_until === 0 ? '(dzisiaj!)' : `(za ${b.days_until} dni)`}</p>
                      </div>
                      {b.phone && (
                        <a href={`tel:${b.phone}`} className="text-gold-600 hover:text-gold-700" title="Zadzwoń">
                          <Phone className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>

          {/* Tasks Panel */}
          <TasksPanel />
        </div>
      )}

      {/* Tasks Panel for non-admin users */}
      {!isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <TasksPanel />
          {/* Upcoming payment deadlines */}
          <Card data-testid="deadlines-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base flex items-center gap-2"><CreditCard className="w-5 h-5 text-amber-500" />Nadchodzące terminy płatności</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/bookings')} className="text-gold-600 text-xs">Zobacz wszystkie <ArrowRight className="w-3 h-3 ml-1" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              {!stats?.upcoming_deadlines?.length ? (
                <p className="text-sm text-muted-foreground py-4 text-center">Brak nadchodzących terminów</p>
              ) : (
                <div className="space-y-2">
                  {stats.upcoming_deadlines.slice(0, 5).map((d, i) => (
                    <div key={i} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                      <div>
                        <p className="font-medium text-sm text-navy-800">{d.booking_number}</p>
                        <p className="text-xs text-muted-foreground">Termin: {fmtDate(d.due_date)}</p>
                      </div>
                      <span className="font-semibold text-amber-700 text-sm">{fmtPLN(d.amount)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}

      {/* Admin: Upcoming payment deadlines */}
      {isAdmin && (
        <Card data-testid="deadlines-card">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base flex items-center gap-2"><CreditCard className="w-5 h-5 text-amber-500" />Nadchodzące terminy płatności</CardTitle>
              <Button variant="ghost" size="sm" onClick={() => navigate('/bookings')} className="text-gold-600 text-xs">Zobacz wszystkie <ArrowRight className="w-3 h-3 ml-1" /></Button>
            </div>
          </CardHeader>
          <CardContent>
            {!stats?.upcoming_deadlines?.length ? (
              <p className="text-sm text-muted-foreground py-4 text-center">Brak nadchodzących terminów</p>
            ) : (
              <div className="space-y-2">
                {stats.upcoming_deadlines.slice(0, 5).map((d, i) => (
                  <div key={i} className="flex items-center justify-between p-3 bg-amber-50 rounded-lg">
                    <div>
                      <p className="font-medium text-sm text-navy-800">{d.booking_number}</p>
                      <p className="text-xs text-muted-foreground">Termin: {fmtDate(d.due_date)}</p>
                    </div>
                    <span className="font-semibold text-amber-700 text-sm">{fmtPLN(d.amount)}</span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Upcoming departures with pre-departure checks */}
      {isAdmin && upcomingDepartures.length > 0 && (
        <Card data-testid="departures-card">
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Plane className="w-5 h-5 text-gold-500" />Najbliższe wyloty (3 dni)</CardTitle>
          </CardHeader>
          <CardContent>
            {loadingDepartures ? (
              <div className="text-center py-4 text-slate-400">Ładowanie...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-navy-800 text-white">
                    <tr>
                      <th className="text-left p-3 font-medium text-xs">Rezerwacja</th>
                      <th className="text-left p-3 font-medium text-xs">Klient</th>
                      <th className="text-left p-3 font-medium text-xs">Kierunek</th>
                      <th className="text-left p-3 font-medium text-xs">Wylot</th>
                      <th className="text-left p-3 font-medium text-xs">Płatność</th>
                      <th className="text-center p-3 font-medium text-slate-600" title="Dokumenty wysłane"><FileCheck className="w-4 h-4 mx-auto" /></th>
                      <th className="text-center p-3 font-medium text-slate-600" title="Klient poinformowany"><UserCheck className="w-4 h-4 mx-auto" /></th>
                      <th className="text-center p-3 font-medium text-slate-600" title="SMS wysłany"><MessageSquare className="w-4 h-4 mx-auto" /></th>
                    </tr>
                  </thead>
                  <tbody>
                    {upcomingDepartures.map((dep) => (
                      <tr key={dep.id} className="border-b hover:bg-slate-50">
                        <td className="p-3 font-medium text-gold-700">{dep.internal_booking_number}</td>
                        <td className="p-3 text-navy-700">{dep.client_name}</td>
                        <td className="p-3 text-slate-700">{dep.destination}</td>
                        <td className="p-3">
                          <span className="text-slate-700">{fmtDate(dep.departure_date)}</span>
                          <Badge className={`ml-2 text-xs ${dep.days_until_departure === 0 ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'}`}>
                            {dep.days_label}
                          </Badge>
                        </td>
                        <td className="p-3">
                          {dep.remaining_balance <= 0 ? (
                            <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">Opłacone</Badge>
                          ) : (
                            <Badge className="bg-red-100 text-red-700 hover:bg-red-100">{fmtPLN(dep.remaining_balance)}</Badge>
                          )}
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <Checkbox
                              checked={dep.documents_sent || false}
                              onCheckedChange={() => handleCheckboxChange(dep.id, 'documents_sent', dep.documents_sent)}
                            />
                            {dep.documents_sent_by && (
                              <span className="text-[10px] text-slate-400" title={`${fmtDate(dep.documents_sent_at?.split('T')[0])} ${dep.documents_sent_at?.split('T')[1]?.slice(0,5)}`}>
                                {dep.documents_sent_by}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <Checkbox
                              checked={dep.client_informed || false}
                              onCheckedChange={() => handleCheckboxChange(dep.id, 'client_informed', dep.client_informed)}
                            />
                            {dep.client_informed_by && (
                              <span className="text-[10px] text-slate-400" title={`${fmtDate(dep.client_informed_at?.split('T')[0])} ${dep.client_informed_at?.split('T')[1]?.slice(0,5)}`}>
                                {dep.client_informed_by}
                              </span>
                            )}
                          </div>
                        </td>
                        <td className="p-3 text-center">
                          <div className="flex flex-col items-center gap-1">
                            <Checkbox
                              checked={dep.sms_sent || false}
                              onCheckedChange={() => handleCheckboxChange(dep.id, 'sms_sent', dep.sms_sent)}
                            />
                            {dep.sms_sent_by && (
                              <span className="text-[10px] text-slate-400" title={`${fmtDate(dep.sms_sent_at?.split('T')[0])} ${dep.sms_sent_at?.split('T')[1]?.slice(0,5)}`}>
                                {dep.sms_sent_by}
                              </span>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Status counts + top employees */}
      {isAdmin && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card data-testid="status-card">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Rezerwacje wg statusu</CardTitle>
                <Button variant="ghost" size="sm" onClick={() => navigate('/bookings')} className="text-gold-600 text-xs">Zobacz wszystkie <ArrowRight className="w-3 h-3 ml-1" /></Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                <div className="flex justify-between items-center p-3 bg-emerald-50 rounded-lg">
                  <span className="text-emerald-700 font-medium">Aktywne</span>
                  <span className="text-emerald-700 font-bold">{stats?.status_counts?.aktywna || 0}</span>
                </div>
                <div className="flex justify-between items-center p-3 bg-red-50 rounded-lg">
                  <span className="text-red-600 font-medium">Anulowane</span>
                  <span className="text-red-600 font-bold">{stats?.status_counts?.anulowana || 0}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card data-testid="top-employees-card">
            <CardHeader className="pb-3">
              <CardTitle className="text-base">Top pracownicy</CardTitle>
            </CardHeader>
            <CardContent>
              {!stats?.top_employees?.length ? (
                <p className="text-sm text-slate-400 py-4 text-center">Brak danych</p>
              ) : (
                <div className="space-y-2">
                  {stats.top_employees.map((emp, i) => (
                    <div key={emp.id} className="flex items-center justify-between p-3 rounded-lg bg-slate-50">
                      <div className="flex items-center gap-3">
                        <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${i === 0 ? 'bg-amber-400 text-white' : 'bg-slate-200 text-slate-600'}`}>{i + 1}</span>
                        <div>
                          <p className="font-medium text-sm text-slate-900">{emp.name}</p>
                          <p className="text-xs text-slate-500">{emp.bookings} rezerwacji</p>
                        </div>
                      </div>
                      <span className="font-semibold text-gold-700 text-sm">{fmtPLN(emp.total_sales)}</span>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};

export default DashboardPage;
